import { useRef, useState, type ChangeEvent } from "react";
import { Title, useNotify, usePermissions } from "react-admin";
import { Alert, Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { hasPermission } from "@/utils/permission-utils";
import {
  BANK_FORMAT_OPTIONS,
  BANK_STATEMENT_ACCEPT,
  defaultBankTallyExportForm,
  isBankFormat,
  validateBankTallyExportForm,
  type BankTallyExportFormState,
} from "./bank-tally-export-form";
import type { BankTallyMapping } from "./build-bank-tally-vouchers";
import BankTallyExportPreviewDialog from "./BankTallyExportPreviewDialog";
import {
  BankParseError,
  bankTallyFilenamePrefix,
  parseBankStatementFile,
  type BankCreditTxn,
  type BankSkippedRow,
} from "./parse-bank-statement";

type PreviewState = {
  credits: BankCreditTxn[];
  skipped: BankSkippedRow[];
  mapping: BankTallyMapping;
  filenamePrefix: string;
};

export default function BankTallyExportPage() {
  const notify = useNotify();
  const { permissions } = usePermissions();
  const canView = hasPermission(permissions, "tallyConfig.view");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [form, setForm] = useState<BankTallyExportFormState>(() => defaultBankTallyExportForm());
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  if (!canView) {
    return (
      <>
        <Title title="Bank → Tally export" />
        <Box sx={{ p: 2, maxWidth: 720 }}>
          <Alert severity="warning">You do not have permission to use bank Tally export.</Alert>
        </Box>
      </>
    );
  }

  const updateMapping = (patch: Partial<BankTallyMapping>) => {
    setForm((prev) => ({ ...prev, mapping: { ...prev.mapping, ...patch } }));
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = "";
    setForm((prev) => ({ ...prev, file }));
  };

  const onParse = async () => {
    const error = validateBankTallyExportForm(form);
    if (error) {
      notify(error, { type: "warning" });
      return;
    }
    const file = form.file;
    if (!file) return;

    setParsing(true);
    try {
      const result = await parseBankStatementFile(form.bankFormat, file);
      const mapping: BankTallyMapping = {
        drLedger: form.mapping.drLedger.trim(),
        crLedger: form.mapping.crLedger.trim(),
        costCategory: form.mapping.costCategory.trim(),
        costCentre: form.mapping.costCentre.trim(),
      };
      setPreview({
        credits: result.credits,
        skipped: result.skipped,
        mapping,
        filenamePrefix: bankTallyFilenamePrefix(form.bankFormat),
      });
      setPreviewOpen(true);
      if (result.credits.length === 0) {
        notify("No credit transactions found in this statement.", { type: "info" });
      }
    } catch (err) {
      const message =
        err instanceof BankParseError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not parse the bank statement.";
      notify(message, { type: "error" });
    } finally {
      setParsing(false);
    }
  };

  return (
    <>
      <Title title="Bank → Tally export" />
      <Box sx={{ p: 2, maxWidth: 720 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Bank → Tally export
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Upload a bank statement, map every credit to one Dr/Cr ledger and cost centre, then
          preview Receipt vouchers for Tally Excel export. Debit rows are skipped. Parsing runs
          entirely in the browser.
        </Alert>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              label="Bank format"
              value={form.bankFormat}
              onChange={(e) => {
                const value = e.target.value;
                if (!isBankFormat(value)) return;
                setForm((prev) => ({ ...prev, bankFormat: value }));
              }}
              fullWidth
              disabled={parsing}
            >
              {BANK_FORMAT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                height: "100%",
                justifyContent: "center",
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={BANK_STATEMENT_ACCEPT}
                hidden
                onChange={onFileChange}
              />
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                disabled={parsing}
              >
                Choose statement file
              </Button>
              <Typography variant="body2" color="text.secondary">
                {form.file ? form.file.name : "Accepted: .xls, .xlsx, .csv"}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Dr ledger"
              value={form.mapping.drLedger}
              onChange={(e) => updateMapping({ drLedger: e.target.value })}
              fullWidth
              required
              disabled={parsing}
              helperText="Usually the bank ledger (debit side of Receipt)"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Cr ledger"
              value={form.mapping.crLedger}
              onChange={(e) => updateMapping({ crLedger: e.target.value })}
              fullWidth
              required
              disabled={parsing}
              helperText="Income / contra ledger credited for each txn"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Cost category"
              value={form.mapping.costCategory}
              onChange={(e) => updateMapping({ costCategory: e.target.value })}
              fullWidth
              required
              disabled={parsing}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Cost centre"
              value={form.mapping.costCentre}
              onChange={(e) => updateMapping({ costCentre: e.target.value })}
              fullWidth
              required
              disabled={parsing}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Button variant="contained" onClick={() => void onParse()} disabled={parsing}>
            {parsing ? "Parsing…" : "Parse & preview"}
          </Button>
        </Box>
      </Box>

      {preview ? (
        <BankTallyExportPreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          credits={preview.credits}
          skipped={preview.skipped}
          mapping={preview.mapping}
          filenamePrefix={preview.filenamePrefix}
        />
      ) : null}
    </>
  );
}
